import type {
  DraftDiffHunk,
  DraftDiffProblem,
  DraftElementDiff,
  DraftFileDiff,
  DraftRevision,
  DraftSourceDiff,
  RevisionDiff,
} from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { usePlatform } from '../app/platform';
import { DRAFT_DIFF_VIEWS, type DraftDiffView } from '../app/router';
import { diffChangeView } from '../app/status';
import { useAsync } from '../app/useAsync';

export interface RevisionDiffPanelProps {
  workspaceId: string;
  appId: string;
  /** 差分を見る版。前の版（parentId）からこの版への差分を出す。 */
  revision: DraftRevision;
  view: DraftDiffView;
  onSelectView(view: DraftDiffView): void;
}

const VIEW_LABELS: Record<DraftDiffView, string> = { file: 'ファイル', element: '要素', source: '出典' };

/**
 * 「この版で何が変わったか」。API は視点ごとに 1 つずつ返すので、視点を切り替えるたびに取り直す（計画の調整 B11）。
 * 計算はすべてサーバー（studio/domain/revision_diff.py）で、ここは描くだけ。
 */
export function RevisionDiffPanel({ workspaceId, appId, revision, view, onSelectView }: RevisionDiffPanelProps) {
  if (revision.parentId === null) {
    return <EmptyState title="取り込んだ最初の版です" description="前の版が無いので差分はありません。" />;
  }
  return (
    <DiffBody
      workspaceId={workspaceId}
      appId={appId}
      parentId={revision.parentId}
      revisionId={revision.id}
      view={view}
      onSelectView={onSelectView}
    />
  );
}

interface DiffBodyProps {
  workspaceId: string;
  appId: string;
  parentId: string;
  revisionId: string;
  view: DraftDiffView;
  onSelectView(view: DraftDiffView): void;
}

function DiffBody({ workspaceId, appId, parentId, revisionId, view, onSelectView }: DiffBodyProps) {
  const { api } = usePlatform();
  const diff = useAsync(
    () => api.getRevisionDiff(workspaceId, appId, parentId, { to: revisionId, view }),
    [api, workspaceId, appId, parentId, revisionId, view],
  );
  // 取り直しの間は前の視点・前の版の結果を出さない。
  const shown = diff.data && diff.data.view === view && diff.data.toRevisionId === revisionId ? diff.data : null;

  return (
    <div className="stack">
      <Tabs
        label="差分の視点"
        active={view}
        onSelect={(key) => onSelectView(key as DraftDiffView)}
        items={DRAFT_DIFF_VIEWS.map((key) => ({ key, label: VIEW_LABELS[key] }))}
      />
      {diff.loading && !shown && <Spinner label="差分を取得しています" />}
      <ProblemNotice problem={diff.problem} action={<Button onClick={diff.reload}>再取得</Button>} />
      {shown && <DiffContent diff={shown} />}
    </div>
  );
}

function DiffContent({ diff }: { diff: RevisionDiff }) {
  return (
    <>
      {diff.problems.length > 0 && <ParseProblems problems={diff.problems} />}
      {diff.view === 'file' && <FileDiffs files={diff.files} />}
      {diff.view === 'element' && <ElementDiffs elements={diff.elements} />}
      {diff.view === 'source' && <SourceDiffs sources={diff.sources} />}
    </>
  );
}

/** 壊れた BPMN / DMN は差分から外れ、ほかのファイルは出る。何が外れたかを隠さない。 */
function ParseProblems({ problems }: { problems: DraftDiffProblem[] }) {
  return (
    <div className="problem problem-notice" role="status">
      <div className="problem-head">
        <strong className="problem-title">解析できなかったファイルがあります</strong>
      </div>
      <ul className="warnings">
        {problems.map((problem) => (
          <li key={`${problem.revisionId}:${problem.path}`}>
            <code>{problem.path}</code>
            {`（版 ${problem.revisionId.slice(0, 8)}）: ${problem.message}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FileDiffs({ files }: { files: DraftFileDiff[] }) {
  if (files.length === 0) return <EmptyState title="ファイルの変更はありません" />;
  return (
    <div className="stack">
      {files.map((file) => {
        const change = diffChangeView(file.change);
        return (
          <section key={file.path} className="diff-file" aria-label={`${file.path} の差分`}>
            <div className="diff-file-head">
              <code>{file.path}</code>
              <Badge tone={change.tone}>{change.label}</Badge>
            </div>
            {file.hunks === null ? (
              <p className="hint diff-omitted">{omittedText(file.omittedReason)}</p>
            ) : file.hunks.length === 0 ? (
              <p className="hint diff-omitted">テキストの変更はありません</p>
            ) : (
              <div className="diff-hunks">
                {file.hunks.map((hunk) => (
                  <HunkView key={`${hunk.oldStart}:${hunk.newStart}`} hunk={hunk} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function HunkView({ hunk }: { hunk: DraftDiffHunk }) {
  // 行番号は hunk の開始から数える。追加の行は前の版に、削除の行は後の版に番号を持たない。
  let oldLine = hunk.oldStart;
  let newLine = hunk.newStart;
  return (
    <div className="diff-hunk">
      <div className="diff-hunk-head">{`@@ -${hunk.oldStart},${hunk.oldLines} +${hunk.newStart},${hunk.newLines} @@`}</div>
      {hunk.lines.map((line, index) => {
        const mark = line.charAt(0);
        const kind = mark === '+' ? 'inserted' : mark === '-' ? 'deleted' : 'context';
        const oldNo = kind === 'inserted' ? '' : String(oldLine++);
        const newNo = kind === 'deleted' ? '' : String(newLine++);
        return (
          <div key={index} className={`diff-line diff-line-${kind}`}>
            <span className="diff-line-no" aria-hidden="true">
              {oldNo}
            </span>
            <span className="diff-line-no" aria-hidden="true">
              {newNo}
            </span>
            <code>{line}</code>
          </div>
        );
      })}
    </div>
  );
}

function omittedText(reason: DraftFileDiff['omittedReason']): string {
  if (reason === 'binary') return '画像などのため差分を表示しません';
  if (reason === 'too_large') return '大きいため差分を表示しません';
  return '差分を表示しません';
}

function ElementDiffs({ elements }: { elements: DraftElementDiff[] }) {
  if (elements.length === 0) return <EmptyState title="BPMN・DMN の要素の変更はありません" />;
  return (
    <Table
      label="要素の差分"
      columns={[
        { key: 'path', header: 'ファイル' },
        { key: 'element', header: '要素' },
        { key: 'type', header: '種類' },
        { key: 'name', header: '名前' },
        { key: 'change', header: '変更' },
        { key: 'fields', header: '変わった項目' },
      ]}
      rows={elements.map((element) => {
        const change = diffChangeView(element.change);
        return {
          key: `${element.path}#${element.elementId}`,
          cells: {
            path: <code>{element.path}</code>,
            element: <code>{element.elementId}</code>,
            type: <code>{element.elementType}</code>,
            name: element.name || '—',
            change: <Badge tone={change.tone}>{change.label}</Badge>,
            fields: element.fields.map(fieldLabel).join('、') || '—',
          },
        };
      })}
    />
  );
}

/** 属性名はそのまま出し、API が付ける 2 つの印（本文・種類）だけを読める言葉にする。 */
function fieldLabel(field: string): string {
  if (field === 'body') return '本文';
  if (field === 'type') return '種類';
  return field;
}

function SourceDiffs({ sources }: { sources: DraftSourceDiff[] }) {
  if (sources.length === 0) return <EmptyState title="出典の変更はありません" />;
  return (
    <Table
      label="出典の差分"
      columns={[
        { key: 'path', header: 'ファイル' },
        { key: 'element', header: '要素' },
        { key: 'relation', header: '関係' },
        { key: 'label', header: '出典' },
        { key: 'change', header: '変更' },
      ]}
      rows={sources.map((source, index) => {
        const change = diffChangeView(source.change);
        return {
          key: `${index}:${source.change}:${source.path}#${source.elementId}:${source.relation}`,
          cells: {
            path: <code>{source.path}</code>,
            element: <code>{source.elementId}</code>,
            relation: <code>{source.relation}</code>,
            label: source.label || '（本文なし）',
            change: <Badge tone={change.tone}>{change.label}</Badge>,
          },
        };
      })}
    />
  );
}
